import {
  ArrowUpRight,
  Briefcase,
  IdentificationCard,
  MapPin,
} from "@phosphor-icons/react";
import { useMemo } from "react";

import { PaginatedDataTable } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import type { LinkedInProfile } from "common/types";

import BlacklistRowMenu from "./BlacklistRowMenu";
import DesktopManageBlacklist from "./DesktopManageBlacklist";

import type { ColumnDef } from "@tanstack/react-table";

interface BlacklistAccountsTableProps {
  profiles: LinkedInProfile[];
  selectedRows: LinkedInProfile[];
  setSelectedRows: (rows: LinkedInProfile[]) => void;
  isLoading: boolean;
}

export default function BlacklistAccountsTable({
  profiles,
  selectedRows,
  setSelectedRows,
  isLoading,
}: BlacklistAccountsTableProps) {
  const columns = useMemo(() => {
    const cols: ColumnDef<LinkedInProfile>[] = [
      {
        id: "account",
        header: "Accounts",
        cell: ({
          row: {
            original: { picture_url, full_name, public_identifier },
          },
        }) => {
          return isLoading ? (
            <>
              <Skeleton className="rounded-full size-8" />
              <Skeleton className="w-28 h-4 ml-1.5" />
            </>
          ) : (
            <div className="flex w-full justify-between items-center gap-x-1.5">
              <div className="flex items-center gap-x-1.5">
                <ProfileImage src={picture_url} size="sm" />

                <div className="flex items-center gap-x-1 min-w-0 flex-1">
                  <span className="text-xs-bold truncate">{full_name}</span>
                </div>
              </div>

              <Button
                intent="iconOnly"
                variant="ghost-black"
                className="ml-auto"
                size="xs"
                asChild
              >
                <a
                  href={`https://linkedin.com/in/${public_identifier}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open LinkedIn profile"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ArrowUpRight className="size-4 text-purple-500" />
                </a>
              </Button>
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "occupation",
        header: "Occupation",
        cell: ({
          row: {
            original: { occupation },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <span className="word-break-all max-w-[500px] line-clamp-2">
              {occupation || "—"}
            </span>
          ),
        meta: { icon: IdentificationCard, className: "min-w-50 max-w-50" },
        enableHiding: false,
      },
      {
        id: "currentCompany",
        header: "Current company",
        cell: ({
          row: {
            original: { company },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <span className="line-clamp-1 break-all">{company || "—"}</span>
          ),
        meta: { icon: Briefcase, className: "min-w-50 max-w-50" },
        enableHiding: false,
      },
      {
        id: "locations",
        header: "Locations",
        cell: ({
          row: {
            original: { location },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-4 w-28" />
          ) : (
            <span className="line-clamp-1 break-all">
              {location?.name ?? "—"}
            </span>
          ),
        meta: { icon: MapPin, className: "min-w-50 max-w-50" },
        enableHiding: false,
      },
      {
        id: "action",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="rounded-full size-8 ml-auto" />
          ) : (
            <BlacklistRowMenu item={original} />
          ),
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ];
    return cols;
  }, [isLoading]);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "blacklist-accounts",
    columns,
  );

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "blacklist-accounts",
      onRowSelectionChange: setSelectedRows,
      selectedRows,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
    reactTableOptions: {
      data: profiles,
      columns,
    },
    isLoading,
  });

  return (
    <PaginatedDataTable
      table={table}
      isLoading={isLoading}
      selectionActionsComponent={
        <DesktopManageBlacklist
          selectedItems={selectedRows}
          resetSelection={() => setSelectedRows([])}
          blacklistType="accounts"
        />
      }
    />
  );
}
